import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Spinner } from '@easydent/shared-ui';
import {
  getCampaigns, createCampaign, setCampaignStatus, getCreatives,
  type Campaign, type Creative,
} from '@/lib/api';

const money = (n: number, c: string) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(n); }
  catch { return `${n.toFixed(2)} ${c}`; }
};
const statusClass: Record<string, string> = {
  Active: 'bg-brand-50 text-brand-ink-strong', Draft: 'bg-subtle text-muted',
  Paused: 'bg-amber-50 text-amber-700', Ended: 'bg-subtle text-muted', Depleted: 'bg-red-50 text-red-600',
};
const iso = (d: Date) => d.toISOString().slice(0, 10);

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Campaign[] | null>(null);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '', creativeId: '', audience: 'ClinicStaff', totalBudget: 50,
    startDate: iso(new Date()), endDate: iso(new Date(Date.now() + 30 * 864e5)),
  });

  const load = () => getCampaigns().then(setRows).catch(() => setError(t('campaigns.loadError', "Could not load campaigns.")));
  useEffect(() => {
    load();
    getCreatives().then((cs) => setCreatives(cs.filter((c) => c.status === 'Approved'))).catch(() => undefined);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await createCampaign({ ...form, totalBudget: Number(form.totalBudget) });
      setCreating(false);
      await load();
    } catch (err) {
      setError((err as { response?: { data?: { title?: string } } })?.response?.data?.title ?? t('campaigns.createError', "Could not create campaign."));
    } finally { setBusy(false); }
  };

  const change = async (c: Campaign, status: string) => {
    setError(null);
    try { await setCampaignStatus(c.id, status); await load(); }
    catch (err) { setError((err as { response?: { data?: { title?: string } } })?.response?.data?.title ?? t('campaigns.updateError', "Could not update.")); }
  };

  if (error && !rows) return <Alert variant="error">{error}</Alert>;
  if (!rows) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-body">{t('campaigns.title', "Campaigns")}</h1>
        <Button onClick={() => setCreating((v) => !v)} disabled={creatives.length === 0}>{creating ? t('common.cancel', "Cancel") : t('campaigns.new', "New campaign")}</Button>
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      {creatives.length === 0 && !creating && (
        <Alert variant="info">{t('campaigns.needCreative', "You need at least one approved creative before you can run a campaign.")}</Alert>
      )}

      {creating && (
        <Card>
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <Input placeholder={t('campaigns.namePlaceholder', "Campaign name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <select value={form.creativeId} onChange={(e) => setForm({ ...form, creativeId: e.target.value })} required
              className="rounded-lg border border-line-strong px-3 py-2 text-sm">
              <option value="">{t('campaigns.chooseCreative', "Choose a creative\u2026")}</option>
              {creatives.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="rounded-lg border border-line-strong px-3 py-2 text-sm">
              <option value="ClinicStaff">{t('campaigns.audienceStaff', "Clinic staff")}</option>
              <option value="Patients">{t('campaigns.audiencePatients', "Patients")}</option>
            </select>
            <Input type="number" min={1} placeholder={t('campaigns.budgetPlaceholder', "Budget (EUR)")} value={form.totalBudget} onChange={(e) => setForm({ ...form, totalBudget: Number(e.target.value) })} required />
            <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
            <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
            <div className="sm:col-span-2"><Button loading={busy} disabled={!form.name || !form.creativeId}>{t('campaigns.create', "Create campaign")}</Button></div>
          </form>
        </Card>
      )}

      {rows.length === 0 ? (
        <Card><p className="py-6 text-center text-sm text-subtle-ink">{t('campaigns.empty', "No campaigns yet.")}</p></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <Card key={c.id}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-body">{c.name}</span>
                    <span className={`rounded px-2 py-0.5 text-xs ${statusClass[c.status] ?? 'bg-subtle'}`}>{c.status}</span>
                  </div>
                  <div className="text-xs text-muted">
                    {c.creativeTitle} · {c.audience === 'ClinicStaff'
                      ? t('campaigns.audienceStaff', 'Clinic staff')
                      : t('campaigns.audiencePatients', 'Patients')} · {c.startDate} → {c.endDate}
                  </div>
                </div>
                <div className="flex gap-2">
                  {c.status === 'Draft' || c.status === 'Paused' ? (
                    <Button size="sm" onClick={() => change(c, 'Active')}>{t('campaigns.activate', "Activate")}</Button>
                  ) : c.status === 'Active' ? (
                    <Button size="sm" variant="ghost" onClick={() => change(c, 'Paused')}>{t('campaigns.pause', "Pause")}</Button>
                  ) : null}
                  {c.status !== 'Ended' && <Button size="sm" variant="ghost" onClick={() => change(c, 'Ended')}>End</Button>}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-3 text-center text-sm">
                <div><div className="font-semibold text-body">{c.impressions}</div><div className="text-xs text-subtle-ink">{t('campaigns.impressions', "Impressions")}</div></div>
                <div><div className="font-semibold text-body">{c.clicks}</div><div className="text-xs text-subtle-ink">{t('campaigns.clicks', "Clicks")}</div></div>
                <div><div className="font-semibold text-body">{money(c.spentAmount, c.currency)}</div><div className="text-xs text-subtle-ink">{t('campaigns.spent', "Spent")}</div></div>
                <div><div className="font-semibold text-body">{money(c.totalBudget, c.currency)}</div><div className="text-xs text-subtle-ink">{t('campaigns.budget', "Budget")}</div></div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignsPage;
